import type { IngestSection } from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../../lib/utils'
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs'

/** Radix tab value standing in for the null "All" selection. */
const ALL_TAB_VALUE = '__all'

interface SectionTabsProps {
  /** Active sections only (merged ones collapse out of the strip). */
  sections: readonly IngestSection[]
  /** Selected section id; null = the "All" job-level view. */
  selected: string | null
  onSelect: (sectionId: string | null) => void
  /** Entity count per section id, derived client-side from the branch compare. */
  entityCounts: ReadonlyMap<string, number>
}

/**
 * The section strip scoping the page's left column: "All" (today's job-level
 * view) plus one read-only tab per active section, each with the entity count
 * that section contributed to the branch. Hidden entirely when the job has no
 * sections (pre-partition, legacy) so the page renders exactly as before.
 * Lifecycle affordances (rename/delete/merge) deliberately don't exist here.
 * Built on the shared Radix tabs primitives (no `TabsContent` — the page
 * decides what the selection scopes), restyled to the pill look.
 */
export function SectionTabs({ sections, selected, onSelect, entityCounts }: SectionTabsProps) {
  if (sections.length === 0) return null

  // The active state must out-specify the shared line-variant's
  // `…data-[state=active]:bg-transparent` (its group-variant selector beats a
  // plain data-[state=active] utility), so the bg override carries the same
  // group-variant prefix — otherwise the selected pill went transparent while
  // keeping inverted text, i.e. invisible.
  const trigger = (id: string | null, label: string, count?: number) => (
    <TabsTrigger
      key={id ?? ALL_TAB_VALUE}
      value={id ?? ALL_TAB_VALUE}
      className={cn(
        'group/section-tab h-auto flex-none gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-11 font-normal whitespace-nowrap text-muted-foreground transition-colors after:hidden hover:bg-muted/50 hover:text-foreground',
        'data-[state=active]:border-border data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-none',
        'group-data-[variant=line]/tabs-list:data-[state=active]:bg-muted',
        'dark:group-data-[variant=line]/tabs-list:data-[state=active]:bg-muted dark:data-[state=active]:border-border dark:data-[state=active]:text-foreground'
      )}
    >
      <span className="max-w-48 truncate">{label}</span>
      {count !== undefined ? (
        <span className="font-mono text-10 tabular-nums text-muted-foreground/60 group-data-[state=active]/section-tab:text-muted-foreground">
          {count}
        </span>
      ) : null}
    </TabsTrigger>
  )

  return (
    <Tabs
      value={selected ?? ALL_TAB_VALUE}
      onValueChange={(value) => onSelect(value === ALL_TAB_VALUE ? null : value)}
      className="gap-0"
    >
      <TabsList
        aria-label="Sections"
        variant="line"
        className="flex w-full flex-wrap justify-start gap-1.5 rounded-none px-0.5 group-data-[orientation=horizontal]/tabs:h-auto"
      >
        {trigger(null, 'All')}
        {sections.map((section) =>
          trigger(section.id, section.name, entityCounts.get(section.id) ?? 0)
        )}
      </TabsList>
    </Tabs>
  )
}

export default SectionTabs
